// TableContainer.js
import React from "react";
import { useReactTable, flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, getFilteredRowModel } from "@tanstack/react-table";
import "bootstrap/dist/css/bootstrap.min.css";
import styles from "../../style/TableContainer.module.css";

/**
 * @param {Object} props
 * @param {import("@tanstack/react-table").ColumnDef<any>[]} props.columns
 * @param {any[]} props.data
 * @param {(row: import("@tanstack/react-table").Row) => object} [props.getTrProps]
 * @param {(row: import("@tanstack/react-table").Cell) => object} [props.getCellProps]
 * @param {number} [props.defaultPageSize=10]
 * @param {string} [props.tableClassName] Additional CSS classes to be applied to the table element.
 * @param {string} [props.containerClassName]  Additional CSS classes to be applied to the container wrapping the table element.
 *
 *
 * @returns
 */
const TableContainer = props => {
  var _props$defaultPageSiz, _props$defaultPageSiz2;
  const {
    columns,
    data
  } = props;
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: (_props$defaultPageSiz = props.defaultPageSize) !== null && _props$defaultPageSiz !== void 0 ? _props$defaultPageSiz : 10
  });
  let config = {
    columns,
    data: data !== null && data !== void 0 ? data : [],
    getFilteredRowModel: getFilteredRowModel(),
    //client side filtering
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    //order doesn't matter anymore!
    state: {
      columnFilters
    }
  };
  if (!props.disablePagination) {
    config.state = {
      ...config.state,
      pagination
    };
    config = {
      ...config,
      getPaginationRowModel: getPaginationRowModel(),
      onPaginationChange: setPagination
    };
  }
  const tableInstance = useReactTable(config);
  return (
    /*#__PURE__*/
    // If you're curious what props we get as a result of calling our getter functions (getTableProps(), getRowProps())
    // Feel free to use console.log()  This will help you better understand how react table works underhood.
    React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: `table-responsive ${props.containerClassName}`
    }, /*#__PURE__*/React.createElement("table", {
      className: `table table-borderless ${props.tableClassName} ${styles.TableContainer}`
    }, /*#__PURE__*/React.createElement("thead", null, tableInstance.getHeaderGroups().map(headerGroup => /*#__PURE__*/React.createElement("tr", {
      key: "tr-h-" + headerGroup.id
    }, headerGroup.headers.map(header => {
      var _asc$desc$header$colu;
      return /*#__PURE__*/React.createElement("th", {
        scope: "col",
        key: "th-h-" + header.id
      }, header.isPlaceholder ? null : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: header.column.getCanSort() ? `cursor-pointer select-none ${styles.TableHeader}` : styles.TableHeader,
        onClick: header.column.getToggleSortingHandler()
      }, flexRender(header.column.columnDef.header, header.getContext()), (_asc$desc$header$colu = {
        asc: " ↑",
        desc: " ↓"
      }[header.column.getIsSorted()]) !== null && _asc$desc$header$colu !== void 0 ? _asc$desc$header$colu : null), header.column.getCanFilter() ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Filter, {
        column: header.column
      })) : null));
    })))), /*#__PURE__*/React.createElement("tbody", null, (!data || data.length === 0) && /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      colSpan: columns.length,
      className: styles.EmptyTable
    }, "Aucune donn\xE9e disponible.")), tableInstance.getRowModel().rows.map(row => {
      let style = {};
      if (props.getTrProps) {
        style = props.getTrProps(row);
      }
      return /*#__PURE__*/React.createElement("tr", {
        key: "tr-r-" + row.id,
        style: style
      }, row.getVisibleCells().map(cell => {
        let style = {};
        if (props.getCellProps) {
          style = props.getCellProps(cell);
        }
        return /*#__PURE__*/React.createElement("td", {
          key: "td-r-" + cell.id,
          style: style
        }, flexRender(cell.column.columnDef.cell, cell.getContext()));
      }));
    })), /*#__PURE__*/React.createElement("tfoot", null, tableInstance.getFooterGroups().map(footerGroup => /*#__PURE__*/React.createElement("tr", {
      key: footerGroup.id
    }, footerGroup.headers.map(header => /*#__PURE__*/React.createElement("th", {
      key: header.id
    }, header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())))))))), !props.disablePagination && /*#__PURE__*/React.createElement("div", {
      className: styles.Pagination
    }, /*#__PURE__*/React.createElement("nav", null, /*#__PURE__*/React.createElement("ul", {
      className: "pagination pagination-lg justify-content-center"
    }, /*#__PURE__*/React.createElement("li", {
      className: "page-item" + (!tableInstance.getCanPreviousPage() && " disabled")
    }, /*#__PURE__*/React.createElement("span", {
      className: "page-link",
      onClick: () => tableInstance.firstPage()
    }, "\xAB")), /*#__PURE__*/React.createElement("li", {
      className: "page-item" + (!tableInstance.getCanPreviousPage() && " disabled")
    }, /*#__PURE__*/React.createElement("span", {
      className: "page-link",
      onClick: () => tableInstance.previousPage()
    }, "\u2039")), /*#__PURE__*/React.createElement("li", {
      className: `page-item ${styles.PageNumber}`
    }, /*#__PURE__*/React.createElement("strong", null, tableInstance.getState().pagination.pageIndex + 1, " ", "sur ", tableInstance.getPageCount().toLocaleString())), /*#__PURE__*/React.createElement("li", {
      className: `page-item ${styles.GoToPage}`
    }, "Aller \xE0 la page", /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      max: tableInstance.getPageCount(),
      defaultValue: tableInstance.getState().pagination.pageIndex + 1,
      onChange: e => {
        const page = e.target.value ? Number(e.target.value) - 1 : 0;
        tableInstance.setPageIndex(page);
      },
      className: "border p-1 rounded w-16",
      disabled: tableInstance.getPageCount() === 1
    }), /*#__PURE__*/React.createElement("select", {
      defaultValue: (_props$defaultPageSiz2 = props.defaultPageSize) !== null && _props$defaultPageSiz2 !== void 0 ? _props$defaultPageSiz2 : tableInstance.getState().pagination.pageSize,
      onChange: e => {
        tableInstance.setPageSize(Number(e.target.value));
      }
    }, [10, 20, 50].map(pageSize => /*#__PURE__*/React.createElement("option", {
      key: pageSize,
      value: pageSize
    }, "Afficher ", pageSize)))), /*#__PURE__*/React.createElement("li", {
      className: "page-item" + (!tableInstance.getCanNextPage() && " disabled")
    }, /*#__PURE__*/React.createElement("span", {
      className: "page-link",
      onClick: () => tableInstance.nextPage()
    }, "\u203A")), /*#__PURE__*/React.createElement("li", {
      className: "page-item" + (!tableInstance.getCanNextPage() && " disabled")
    }, /*#__PURE__*/React.createElement("span", {
      className: "page-link",
      onClick: () => tableInstance.lastPage()
    }, "\xBB"))))))
  );
};
function Filter(_ref) {
  let {
    column
  } = _ref;
  const columnFilterValue = column.getFilterValue();
  return /*#__PURE__*/React.createElement("input", {
    type: "text",
    className: "w-36 border rounded",
    placeholder: `...`,
    value: columnFilterValue !== null && columnFilterValue !== void 0 ? columnFilterValue : "",
    onChange: e => column.setFilterValue(e.target.value),
    onClick: e => e.stopPropagation()
  });
}
export default TableContainer;